import React, { JSX, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Panel from "../../components/Panel";
import useFingerprint from "../../hooks/useFingerprint";
import useSyncClient from "../../hooks/useSyncClient";
import { useAppDispatch } from "../../store/hooks";
import { notificationAdd } from "../../actions/notification";

export const ProgressBar = ({ value, max }: { value: number; max: number }): JSX.Element => (
  <Box
    sx={{
      position: "relative",
      background: "#e5e7eb",
      borderRadius: 2,
      height: "20px",
      margin: "10px 0",
      overflow: "hidden"
    }}
  >
    <Box
      sx={{
        background: "#2563eb",
        height: "100%",
        transition: "width .3s",
        width: `${(value / max) * 100}%`
      }}
    />
    <Box
      component="span"
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        color: "#1e3a8a"
      }}
    >
      {value}/{max} scans
    </Box>
  </Box>
);

interface Props {
  onClose: () => void;
  clientId?: number;
}

const FingerprintEnrollPanel = ({ onClose, clientId }: Props): JSX.Element => {
  const {
    deviceReady,
    imageB64,
    enrollState,
    loading,
    error,
    initialize,
    terminate,
    startEnroll,
    cancelEnroll,
    saveEnrolledTemplate
  } = useFingerprint();

  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const { syncClient } = useSyncClient();

  const handleSave = async () => {
    if (!enrollState?.templateBase64 || !clientId) return;
    setSaveMsg(null);
    try {
      const result = await saveEnrolledTemplate(clientId, enrollState.templateBase64);
      if (result) {
        setSaveMsg(`Saved — FID ${result.id} assigned`);
        syncClient({
          clientId: result.clientId,
          toUpdateValue: result,
          toUpdateKey: "fingerprintTemplate"
        });
      }
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Fingerprint enrolled successfully.",
          autoTimeout: true
        })
      );
      onClose();
    } catch (e) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Could not save fingerprint template. Try again later.",
          autoTimeout: true
        })
      );
    }
  };

  return (
    <Panel title="Fingerprint Enrollment" onClose={onClose}>
      <Box px={2} maxWidth="500px">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              flexShrink: 0,
              background: deviceReady ? "#22c55e" : "#ef4444"
            }}
          />
          <Typography sx={{ flex: 1, fontSize: 14, color: "#374151" }}>
            {deviceReady ? "Device ready" : "Device not connected"}
          </Typography>
          {!deviceReady ? (
            <Button
              variant="outlined"
              onClick={initialize}
              disabled={loading}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Initialize Device
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={terminate}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Close Device
            </Button>
          )}
        </Box>

        {error && (
          <Typography
            sx={{
              background: "#fee2e2",
              color: "#b91c1c",
              padding: "8px 12px",
              borderRadius: 1,
              fontSize: 13,
              marginBottom: 1
            }}
          >
            {error}
          </Typography>
        )}

        <Box
          sx={{
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            height: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 1,
            overflow: "hidden"
          }}
        >
          {imageB64 ? (
            <img
              src={`data:image/jpeg;base64,${imageB64}`}
              alt="Live scan"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <Typography sx={{ color: "#9ca3af", fontSize: 13 }}>
              Live preview appears here
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            padding: 2
          }}
        >
          {!enrollState?.active && !enrollState?.done && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={startEnroll}
              disabled={!deviceReady || !clientId}
            >
              Start Enrollment
            </Button>
          )}

          {enrollState?.active && (
            <>
              <ProgressBar value={enrollState.samplesCollected} max={enrollState.samplesRequired} />
              <Typography variant="body2" gutterBottom>
                {enrollState.message}
              </Typography>
              <Button color="error" onClick={cancelEnroll}>
                Cancel
              </Button>
            </>
          )}

          {enrollState?.done && enrollState.success && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2">3 scans complete</Typography>
              <Typography>Template ready </Typography>
              <Button variant="outlined" color="primary" onClick={handleSave} disabled={loading}>
                {loading ? "Saving…" : "Save fingerprint"}
              </Button>
              {saveMsg && <Typography variant="subtitle2">{saveMsg}</Typography>}
            </Box>
          )}

          {enrollState?.done && !enrollState.success && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="error">
                {enrollState.message}
              </Typography>
              <Button onClick={startEnroll}>Try Again</Button>
            </Box>
          )}
        </Box>
      </Box>
    </Panel>
  );
};

export default FingerprintEnrollPanel;
